import { ref, shallowRef, computed } from '@vue/composition-api';
import { isNil } from 'lodash';
import { useInstance } from 'common-module/src/util/useInstance';

import { 
  checkDeleteField, 
  deleteField as apiDeleteField
} from '../api/field';
import { LayoutFieldItem } from '../model/layout';

interface issueTypeVOSModel {
  issueTypeId: number;
  issueTypeName: string;
}

interface relatedFieldsModel {
  fieldName: string;
}

interface CheckDeleteResult {
  canDelete: boolean;
  issueTypeVOS: issueTypeVOSModel[];
  msg: string;
  relatedFields: relatedFieldsModel[];
}

export const useDeleteField = (
  projectId: number | undefined,
  issueTypeId: number
) => {
  if (isNil(projectId) || isNil(issueTypeId)) {
    return;
  }

  const instance = useInstance();
  const checkDeleteInfo = shallowRef<CheckDeleteResult | null>(null);
  const canDelete = computed<boolean>(() => checkDeleteInfo.value?.canDelete || false);
  // const currentDeleteField = shallowRef<LayoutFieldItem | null>(null);
  const deleteLoading = ref<boolean>(false);

  // 校验是否能删除字段
  const isCheckDeleteField = async (field: LayoutFieldItem): Promise<CheckDeleteResult | null> => {
    const { customFieldId, customFieldName } = field || {};
    if (isNil(projectId) || isNil(customFieldId)) {
      console.warn('Missing parameter');
      return null;
    }
    return await checkDeleteField({
      projectId,
      customFieldId: customFieldId,
      fieldName: customFieldName
    });
  };

  const checkDeleteLoading = ref(false);
  const onDelete = async (
    field: LayoutFieldItem,
    callback: () => void
  ): Promise<void> => {
    // currentDeleteField.value = field;
    try {
      checkDeleteLoading.value = true;
      checkDeleteInfo.value = await isCheckDeleteField(field);
    } finally {
      checkDeleteLoading.value = false;
    }
  
    try {
      await sureDeleteMsgBox({
        issueTypeVOS: checkDeleteInfo.value?.issueTypeVOS || [], 
        relatedFields: checkDeleteInfo.value?.relatedFields || [], 
        fieldName: field.customFieldName 
      });
      if (!canDelete.value) {
        return;
      }
      deleteLoading.value = true;
      await apiDeleteField({ id: field.customFieldId });
      instance.$message.success(instance.$t('project.issueType.layout.deleteMsg', [field.customFieldName]));
      typeof callback === 'function' && callback();
    } catch (e) {
      console.log(e);
    } finally {
      deleteLoading.value = false;
    }
  };

  const sureDeleteMsgBox = ({ 
    issueTypeVOS = [], 
    relatedFields = [], 
    fieldName 
  }: {
    issueTypeVOS: issueTypeVOSModel[];
    relatedFields: relatedFieldsModel[];
    fieldName: string;
  }) => {
    console.log(canDelete.value, issueTypeVOS, relatedFields, fieldName);
    const isCurrentLayout = issueTypeVOS?.some(i => i.issueTypeId === issueTypeId) && issueTypeVOS?.length === 1;
    const issueTypeNames = issueTypeVOS?.map(i => `【${i.issueTypeName}】`).join('');
    const relatedNames = relatedFields?.map(f => `「${f.fieldName}」`).join('');

    const title = instance.$t('project.issueType.layout.deleteDialog.title');
    const sureDeleteMsg = instance.$t('project.issueType.layout.deleteDialog.msg', [fieldName]); // 可以删除, 提示用户
    const canNotDeleteMsg = instance.$t('project.issueType.layout.deleteDialog.canNotDelete', { issueTypeNames, fieldName }); // 在非当前布局使用，不可删除提示
    const savePromptMsg = instance.$t('project.issueType.layout.deleteDialog.savePrompt', { fieldName }); // 在当前布局使用，保存提示
    const relatedMsg = instance.$t('project.issueType.layout.deleteDialog.relatedField', { fieldName, relatedNames }); // 被其他字段（如：公式）依赖提示
    const issueTypeMsg = isCurrentLayout ? savePromptMsg : canNotDeleteMsg;

    const isDelete = !issueTypeVOS?.length && !relatedFields?.length;
    const message = isDelete ? sureDeleteMsg : (issueTypeVOS?.length ? issueTypeMsg : relatedMsg);
    return instance.$msgbox({
      title,
      message,
      dangerouslyUseHTMLString: !canDelete.value,
      confirmButtonText: canDelete.value ? instance.$t('button.delete') : instance.$t('button.confirm'),
      cancelButtonText: instance.$t('button.cancel'),
      showCancelButton: canDelete.value,
      confirmButtonClass: 'el-button--danger'
    });
  };

  return {
    isCheckDeleteField,
    onDelete,
    deleteLoading,
    checkDeleteLoading
  };
};

export type DeleteFieldStore = ReturnType<typeof useDeleteField>;